import { TODO } from '@b2b/types/core';
import { StateService } from '@uirouter/angularjs';
import angular, { IQService, IScope, ITimeoutService } from 'angular';

type SelectedRow = { selected: boolean; data: { id: string; name?: string; active: boolean } };

OrdersModalsRequestController.$inject = [
  '$scope',
  '$state',
  '$uibModalInstance',
  '$timeout',
  '$q',
  'ApiService',
  'ValidateService',
  'GridStateFactory',
  'GridDefinitionFactory',
  'UserPreferencesService',
  'customFields',
  'eventTemplates',
  'HelpersService',
  'TemplatesHelpersService',
];

function OrdersModalsRequestController(
  $scope: IScope & { requestModal: TODO },
  $state: StateService,
  $uibModalInstance: TODO,
  $timeout: ITimeoutService,
  $q: IQService,
  ApiService: TODO,
  ValidateService: TODO,
  GridStateFactory: TODO,
  GridDefinitionFactory: TODO,
  UserPreferencesService: TODO,
  customFields: TODO[],
  eventTemplates: { id: string }[],
  HelpersService: TODO,
  TemplatesHelpersService: TODO
) {
  const ctrl = {
    gridState: new GridStateFactory(),
    gridDefinition: new GridDefinitionFactory(),
    dateFormat: UserPreferencesService.getDateFormat('fieldDate', 'dateFormat'),
    eventListGridName: $state.current.name + '.grid0',
    orderId: '',
    viewId: '',
    searchQuery: '',

    isLoaded: false,
    isRightSideListLoading: true,

    model: [] as TODO[],
    selectedRows: {} as Record<string, SelectedRow>,
    selectedRowsVM: [] as SelectedRow['data'][],
    customFields: (customFields || []) as TODO[],
    eventTemplates: (eventTemplates || []) as { id: string; name?: string }[],
    optionsOverride: {
      data: 'requestModal.model',
      enableRowSelection: true,
      multiSelect: true,
      noUnselect: false,
      useAccessibleGrid: true,
      paginationPageSize: 10,
      paginationPageSizes: [10, 25, 50, 100, 150, 200],
      columnDefs: [
        {
          field: 'name',
          displayName: 'Event Name',
          visible: true,
          cellTemplate:
            '<div class="ui-grid-cell-contents" style="position: relative;padding-left: 10px;"><span data-ng-style="row.entity.colorCodeStyle"></span> {{ row.entity.name }} </div>',
        },
      ] as TODO[],
      appScopeProvider: {
        onDataGridPaginate: (newPageNumber, newPageSize, sortField, sortDirection) => {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: (grid, columns, page) => {
          ctrl.onDataGridSortChange(grid, columns);
        },
        onDataGridSelectionChange: (row, e) => {
          ctrl.onDataGridSelectionChange(row);
        },
      },
    },

    init() {
      ctrl.orderId = $state.params.orderId ?? '';
      ctrl.viewId = $state.current.name ?? '';
      ctrl.setCustomFields(customFields);
      ctrl.processEventTemplates();
      ctrl.processEventTemplatesDetails();

      ctrl.gridDefinition.init(
        ctrl.eventListGridName,
        ctrl.optionsOverride,
        ctrl.viewId,
        $state.current.data.enablePreferences
      );

      $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData);
    },

    updateSelectedRowsVM() {
      ctrl.selectedRowsVM = Object.keys(ctrl.selectedRows)
        .filter((rowId) => ctrl.selectedRows[rowId].selected)
        .map((rowId) => ctrl.selectedRows[rowId].data)
        .sort((a, b) => {
          if (a.name && b.name) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          }

          return 0;
        });
    },

    unselectRow(row: SelectedRow['data']) {
      ctrl.selectedRows[row.id].selected = false;
      ctrl.selectedRows[row.id].data.active = false;

      $scope.$broadcast('DataGrid::unselectRow', { row });

      ctrl.updateSelectedRowsVM();
    },

    unselectAllRows() {
      for (const eventId in ctrl.selectedRows) {
        ctrl.selectedRows[eventId].selected = false;
        ctrl.selectedRows[eventId].data.active = false;
      }

      $scope.$broadcast('DataGrid::clearSelection');

      ctrl.updateSelectedRowsVM();
    },

    processEventTemplates() {
      ctrl.eventTemplates.forEach((template) => {
        const entity = {
          selected: true,
          data: {
            id: template.id,
            name: template.name,
            active: true,
          },
        };

        ctrl.selectedRows[template.id] = entity;
      });

      ctrl.updateSelectedRowsVM();
    },

    /**
     * Fetches the event templates details for preselected templates so that we can display
     * their name in the right side list.
     */
    processEventTemplatesDetails() {
      ctrl.isRightSideListLoading = true;

      const promises = ctrl.eventTemplates.map((eventTemplate) => {
        return ApiService.program.get(eventTemplate.id, true);
      });

      $q.all(promises)
        .then((responses) => {
          const templates = responses.map((response) => response.data.payload);

          for (const template of templates) {
            ctrl.eventTemplates.forEach((eventTemplate) => {
              if (eventTemplate.id === template.id) {
                eventTemplate.name = template.name;
              }
            });
          }
        })
        .finally(() => {
          ctrl.processEventTemplates();
          ctrl.isRightSideListLoading = false;
        });
    },

    setCustomFields(customFieldsData: TODO[]) {
      customFieldsData.forEach((customField) => {
        ctrl.optionsOverride.columnDefs.push({
          field: customField.internalLabel,
          displayName: customField.label,
          visible: false,
          colId: customField.label,
        });
      });
    },

    initData() {
      const props = ctrl.gridDefinition.getGridState();
      ctrl.gridState.updateGridState(props.page, props.pageSize, props.sortField, props.sortDirection);

      ctrl.getEvents();
    },

    getEvents() {
      ctrl.gridState.setPropertyValue('search', ctrl.searchQuery);
      ApiService.program.list(false, ctrl.gridState.getGridState()).then(ctrl.success.getEvents, ctrl.error.getEvents);
    },

    cancel() {
      $uibModalInstance.dismiss();
    },

    getSelectedEventTemplates() {
      const selectedEvents: TODO[] = [];

      for (const eventId in ctrl.selectedRows) {
        const event = ctrl.selectedRows[eventId];

        if (event?.selected) {
          selectedEvents.push(event.data);
        }
      }

      return selectedEvents;
    },

    add() {
      $uibModalInstance.close(ctrl.getSelectedEventTemplates());
    },

    search: HelpersService.utils.debounce(() => {
      ctrl.gridState.setPropertyValue('search', ctrl.searchQuery);
      ctrl.getEvents();
    }, 500),

    process(eventTemplates: TODO[]) {
      for (const eventTemplate of eventTemplates) {
        if (eventTemplate.colorCategory && eventTemplate.colorCategory.textColor) {
          eventTemplate.colorCodeStyle = TemplatesHelpersService.getColorCategoryStyle(eventTemplate.colorCategory);
        }

        for (const customField of ctrl.customFields) {
          eventTemplate[customField.internalLabel] = HelpersService.getCustomFieldValue(
            eventTemplate,
            customField.label
          );
        }
      }

      return eventTemplates;
    },

    selectTemplates() {
      $scope.$broadcast('DataGrid::clearSelection');

      const selected: TODO[] = [];

      ctrl.model.forEach((item) => {
        if (ctrl.selectedRows[item.id]?.selected) {
          selected.push(item);
        }
      });

      if (selected.length) {
        $scope.$broadcast('DataGrid::SetSelection', selected);
      }
    },

    onDataGridSelectionChange(maybeRows: TODO | TODO[]) {
      const rows = Array.isArray(maybeRows) ? maybeRows : [maybeRows];

      for (const row of rows) {
        row.entity.active = row.isSelected;
        ctrl.selectedRows[row.entity.id] = { selected: row.isSelected, data: row.entity };
      }

      ctrl.updateSelectedRowsVM();
    },

    onDataGridPaginate(newPage, pageSize, sortField, sortDirection) {
      ctrl.gridState.updateGridState(newPage, pageSize);
      if (sortField) {
        ctrl.gridState.updateGridState(null, null, sortField, null);
      }
      if (sortDirection) {
        ctrl.gridState.updateGridState(null, null, null, sortDirection);
      }
      ctrl.search();
    },

    onDataGridSortChange(grid, columns) {
      const data = columns[0];
      if (data) {
        ctrl.gridState.updateGridState(null, null, data.field, data.sort.direction);
        ctrl.getEvents();
      }
    },

    success: {
      init: (response) => {},
      getEvents: (response) => {
        ctrl.model = ctrl.process(response.data.payload.list);

        if (!$scope.$$phase) {
          $scope.$apply();
        }

        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
        $timeout(() => {
          $scope.$broadcast('DataGrid::ResizeGrid', {
            name: ctrl.gridDefinition.options.gridName,
            datasetLength: ctrl.model.length,
            totalItems: response.data.payload.pagination.count,
          });

          $timeout(() => {
            ctrl.selectTemplates();
          });
        }, 10);
      },
    },

    error: {
      init: (response) => {},
      getEvents: () => {},
    },
  };

  $scope.requestModal = ctrl;
  ctrl.init();
}

export const OrdersModalsRequestControllerModule = angular
  .module('acme.view.application.orders.modals.request.controllers', [
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    'acme.common.service.userPreferences',
    'acme.common.service.helpers',
    'acme.common.service.templates.helpers',
  ])
  .controller('OrdersModalsRequestController', OrdersModalsRequestController).name;
